import {
  AxiosInstance,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { useStepUpStore } from "@/stores/StepUpStore";
import { ERROR_CODE, HTTP_STATUS } from "./constants";
import { appConfig } from "@/config/app";
import {
  shouldReportApiError,
  buildApiErrorReport,
  isTransientNetworkError,
} from "./classifyApiError";
import { reportError } from "@/lib/errorReporter";
import { useSocketStore } from "@/stores/SocketStore";

type TimedRequestConfig = InternalAxiosRequestConfig & {
  metadata?: { startTime: number };
};

interface APIError {
  code: string;
  message: string;
}

interface ErrorInfo {
  status: number;
  code: string;
}

const isUnauthenticated = (errorInfo: ErrorInfo): boolean => {
  return (
    errorInfo.code === ERROR_CODE.UNAUTHENTICATED &&
    errorInfo.status === HTTP_STATUS.UNAUTHORIZED
  );
};

const getErrorInfo = (error: AxiosError): ErrorInfo => {
  if (!error.response || !error.response.data) {
    return { status: 0, code: "unknown" };
  }

  const data = error.response.data as APIError;
  return {
    status: error.response.status,
    code: data.code,
  };
};

type OriginalRequest = InternalAxiosRequestConfig & {
  _stepUpRetried?: boolean;
};

const requestInterceptor = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  (config as TimedRequestConfig).metadata = {
    startTime:
      typeof performance !== "undefined" ? performance.now() : Date.now()
  };
  return config;
};

function isStepUpRequired(
  config: OriginalRequest | undefined,
  errorInfo: ErrorInfo
): config is OriginalRequest {
  if (!config || config._stepUpRetried) {
    return false;
  }

  return (
    errorInfo.status === HTTP_STATUS.FORBIDDEN &&
    errorInfo.code === ERROR_CODE.STEP_UP_REQUIRED
  );
}

const errorInterceptor = async (error: AxiosError): Promise<any> => {
  const originalRequest = error.config;
  const errorInfo = getErrorInfo(error);

  // A transient, no-response network blip is intentionally NOT reported here:
  // the QueryClient retries it and only reports if every retry fails (a real
  // outage). Server-answered failures (5xx/408/429) still report immediately.
  if (shouldReportApiError(error) && !isTransientNetworkError(error)) {
    const timed = error.config as TimedRequestConfig | undefined;
    const startTime = timed?.metadata?.startTime ?? null;
    const durationMs =
      startTime !== null
        ? Math.round(
            (typeof performance !== "undefined"
              ? performance.now()
              : Date.now()) - startTime
          )
        : null;
    const socket = useSocketStore.getState().socket;
    const report = buildApiErrorReport(
      error,
      { connected: socket.connected, id: socket.id ?? null },
      durationMs
    );
    reportError(report);
  }

  if (isUnauthenticated(errorInfo)) {
    const navigation = await import("@/utils/navigation");
    navigation.clientRedirect(appConfig.unauthenticatedEntry);
    return new Promise(() => {});
  }

  if (isStepUpRequired(originalRequest, errorInfo)) {
    originalRequest._stepUpRetried = true;

    const success = await useStepUpStore.getState().prompt();
    if (!success) {
      return Promise.reject(error);
    }

    const { api } = await import("@/api");
    return api.client(originalRequest);
  }

  return Promise.reject(error);
};

export function setupAxiosInterceptors(axiosInstance: AxiosInstance): void {
  axiosInstance.interceptors.request.use(requestInterceptor, (error) =>
    Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    errorInterceptor
  );
}
